import { useScrollContent } from '@exam-management/common/hooks';
import {
  useSaveInDevelopmentQuestionMutation,
  GetQuestionCreationTaskByIdDocument,
  QuestionStatus,
} from '@exam-management/graphql';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate } from '@exam-management/router';
import { QuestionCreationWorkflowStepCodeObject } from '@exam-management/task-management';
import { Button, Flex } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { matchPath, useLocation } from 'react-router-dom';

import { useSingleQuestionDetails } from '../../providers';
import { parseQuestionFormDataForApi } from '../single-question-creation-form';

import type { SingleQuestionCreationEditFormInput } from './single-question-creation.schema';

export default function SingleQuestionCreationEditFormActions() {
  const { showSnackbar } = useSnackbar();

  const { task, roles, status } = useQuestionCreationTaskDetails();
  const { scrollTo } = useScrollContent();
  const formContext = useFormContext<SingleQuestionCreationEditFormInput>();

  const { formState } = formContext;
  const { isSubmitSuccessful } = formState;

  const navigate = useNavigate();
  const { singleQuestion } = useSingleQuestionDetails();

  const isInDevelopment = singleQuestion.status === QuestionStatus.InDevelopment;

  const isEditable =
    isInDevelopment &&
    roles.isAssignee &&
    status.code === QuestionCreationWorkflowStepCodeObject.QuestionCreationInProgress;

  const location = useLocation();
  const matchedTaskManagement = matchPath('/task-management/*', location.pathname);

  const [saveInDevelopmentQuestion, { loading: isLoading }] = useSaveInDevelopmentQuestionMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  });

  const createSingleQuestion = async (data: SingleQuestionCreationEditFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;
    const qBankStructureId = task.metadata?.requirement.qBankStructure?.id;

    if (!qBankStructureLevelItemId || !qBankStructureId)
      throw Error('Invalid qbs id and level item id');

    const payload = parseQuestionFormDataForApi(data, parseContentForApi);

    const result = await saveInDevelopmentQuestion({
      variables: {
        input: {
          qBankStructureLevelItemId,
          data: payload,
          id: singleQuestion.id,
        },
        taskId: task.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      onCompleted: () => {
        scrollTo({ x: 0, y: 0 });
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });
      },
    });

    if (result.errors) throw result.errors;
  };

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const saveAndCreateNew = async (data: SingleQuestionCreationEditFormInput) => {
    const pathToNavigate = matchedTaskManagement
      ? '/task-management/question-creation/:taskId/single/create'
      : '/my-task/question-creation/:taskId/single/create';

    await createSingleQuestion(data);

    navigate(pathToNavigate, {
      params: {
        taskId: task.id,
      },
    });
  };

  const handleSaveAndCreateNew = async () => {
    await formContext.handleSubmit(saveAndCreateNew, handleShowRequiredFieldsError)();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [isSubmitSuccessful, formContext]);

  if (!isEditable) {
    return null;
  }

  const questionCreationCount = task.metadata?.requirement.questionCreationCount ?? 0;
  const requiredQuestionCount = task.metadata?.requirement.requiredQuestionCount ?? 0;

  const shouldAllowSaveAndCreateNew = requiredQuestionCount !== questionCreationCount;

  return (
    <Flex sx={{ gap: 4 }}>
      <Button
        type='submit'
        disabled={formContext.formState.isSubmitting}
        // NOTE: This is a workaround to show loading status for only one button
        loading={formContext.formState.isSubmitting}
      >
        Save
      </Button>
      {shouldAllowSaveAndCreateNew ? (
        <Button
          type='button'
          color='secondary'
          variant='outlined'
          disabled={formContext.formState.isSubmitting}
          loading={isLoading}
          onClick={handleSaveAndCreateNew}
        >
          Save & Create New
        </Button>
      ) : null}
    </Flex>
  );
}
